import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { Plus, Trash2, Edit3, X, Save, Loader2, Eye, EyeOff } from "lucide-react";
import { api, apiErrToStr } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import Licenses from "@/pages/Licenses";

const TABS = [
  { id: "general", label: "Geral" },
  { id: "routing", label: "Calls & SMS Routing" },
  { id: "config", label: "Configuracoes" },
];

const splitList = (value) => Array.isArray(value) ? value.join(", ") : (value || "");
const toList = (value) => String(value || "").split(/[\s,;]+/).map((item) => item.trim().toLowerCase()).filter(Boolean);

const DEFAULT_FORM = {
  codigo_cliente: "",
  nome: "",
  descricao: "",
  slug: "",
  domains: "",
  ips: "",
  admin_name: "Administrador",
  admin_email: "",
  admin_password: "",
  assign_existing_admin: false,
  admin_profile: "tenant_admin",
  startup_dialog: "dashboard",
  send_welcome_email: true,
  sip_auth_prefix: "",
  use_prefixed_sip_auth: true,
  allow_ip_registration: false,
  ddd: "",
  outbound_cid: "",
  branches: [],
  public_monitor_enabled: false,
  public_monitor_username: "",
  public_monitor_password: "",
  license_status: "active",
  license_expires_at: "",
  max_extensions: 100,
  max_trunks: 10,
  max_channels: 100,
  max_channels_inbound: 0,
  max_channels_outbound: 0,
  max_agents: 50,
  max_callcenter: 20,
  max_queues: 20,
  max_supervisors: 10,
  max_queue_resources: 20,
  max_ivrs: 20,
  max_conferences: 20,
  max_parkings: 20,
  max_connect_devices: 0,
  recording_enabled: true,
  recording_retention_days: 30,
  enabled: true,
};

const DEFAULT_BRANCH = {
  id: "",
  nome: "",
  descricao: "",
  sip_auth_prefix: "",
  max_channels_inbound: 0,
  max_channels_outbound: 0,
  allowed_branch_ids: [],
  enabled: true,
};

function Field({ label, children, hint, full = false }) {
  return (
    <div className={full ? "col-span-1 xl:col-span-2" : "col-span-1"}>
      <label className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)] font-semibold block mb-1.5">
        {label}
      </label>
      {children}
      {hint && <div className="text-[10px] text-[color:var(--text-muted)] mt-1 font-mono">{hint}</div>}
    </div>
  );
}

function Toggle({ value, onChange, trueLabel = "Sim", falseLabel = "Nao" }) {
  return (
    <button
      type="button"
      className={`sbc-btn ${value ? "sbc-btn-primary" : ""}`}
      onClick={() => onChange(!value)}
    >
      {value ? trueLabel : falseLabel}
    </button>
  );
}

export default function Tenants() {
  const location = useLocation();
  const { user, tenants: authTenants = [] } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(DEFAULT_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState("general");
  const [showAdminPassword, setShowAdminPassword] = useState(false);
  const [showPublicMonitorPassword, setShowPublicMonitorPassword] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/tenants", {
        params: { _ts: Date.now() },
        headers: { "x-active-tenant-id": "" },
      });
      const nextRows = Array.isArray(data) ? data : [];
      if (nextRows.length) {
        setRows(nextRows);
      } else if (String(user?.role || "").toLowerCase() === "superadmin" && authTenants.length) {
        setRows(authTenants);
      } else {
        setRows([]);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.role, authTenants.length]);

  const open = (row = null) => {
    setError("");
    setActiveTab("general");
    setEditing(row);
    const next = row ? {
      ...DEFAULT_FORM,
      ...row,
      admin_email: row.primary_admin_email || row.admin_email || "",
      domains: splitList(row.domains),
      ips: splitList(row.ips),
      license_expires_at: row.license_expires_at || "",
    } : { ...DEFAULT_FORM };
    setForm(next);
    setShowAdminPassword(false);
    setShowPublicMonitorPassword(false);
    setShowForm(true);
  };

  const close = () => {
    setShowForm(false);
    setEditing(null);
  };

  const setField = (key, value) => setForm((current) => ({ ...current, [key]: value }));
  const addBranch = () => setForm((current) => ({
    ...current,
    branches: [...(current.branches || []), { ...DEFAULT_BRANCH }],
  }));
  const updateBranch = (index, key, value) => setForm((current) => ({
    ...current,
    branches: (current.branches || []).map((branch, branchIndex) => (
      branchIndex === index ? { ...branch, [key]: value } : branch
    )),
  }));
  const removeBranch = (index) => setForm((current) => ({
    ...current,
    branches: (current.branches || []).filter((_, branchIndex) => branchIndex !== index),
  }));

  const payload = useMemo(() => ({
    ...form,
    admin_email: String(form.admin_email || "").trim() || null,
    public_monitor_username: String(form.public_monitor_username || "").trim() || null,
    public_monitor_password: String(form.public_monitor_password || "").trim() || null,
    domains: toList(form.domains),
    ips: toList(form.ips),
    branches: (form.branches || []).map((branch) => ({
      id: String(branch.id || "").trim(),
      nome: String(branch.nome || "").trim(),
      descricao: String(branch.descricao || "").trim(),
      sip_auth_prefix: String(branch.sip_auth_prefix || "").trim(),
      max_channels_inbound: Number(branch.max_channels_inbound || 0),
      max_channels_outbound: Number(branch.max_channels_outbound || 0),
      allowed_branch_ids: Array.isArray(branch.allowed_branch_ids) ? branch.allowed_branch_ids : [],
      enabled: !!branch.enabled,
    })).filter((branch) => branch.nome),
    recording_retention_days: Number(form.recording_retention_days || 30),
    max_extensions: Number(form.max_extensions || 0),
    max_trunks: Number(form.max_trunks || 0),
    max_channels: Number(form.max_channels || 0),
    max_channels_inbound: Number(form.max_channels_inbound || 0),
    max_channels_outbound: Number(form.max_channels_outbound || 0),
    max_agents: Number(form.max_agents || 0),
    max_callcenter: Number(form.max_callcenter || 0),
    max_queues: Number(form.max_queues || 0),
    max_supervisors: Number(form.max_supervisors || 0),
    max_queue_resources: Number(form.max_queue_resources || 0),
    max_ivrs: Number(form.max_ivrs || 0),
    max_conferences: Number(form.max_conferences || 0),
    max_parkings: Number(form.max_parkings || 0),
    max_connect_devices: Number(form.max_connect_devices || 0),
    license_expires_at: form.license_expires_at || null,
  }), [form]);

  const save = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError("");
    try {
      if (editing) await api.put(`/tenants/${editing.id}`, payload);
      else await api.post("/tenants", payload);
      close();
      window.dispatchEvent(new Event("voxyra-ipbx-config-pending"));
      await load();
    } catch (err) {
      setError(apiErrToStr(err?.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Confirma exclusao?")) return;
    try {
      await api.delete(`/tenants/${id}`);
      await load();
      window.dispatchEvent(new Event("voxyra-ipbx-config-pending"));
    } catch (err) {
      setError(apiErrToStr(err?.response?.data?.detail) || err.message);
    }
  };

  if (location.pathname === "/clientes/licencas") {
    return <Licenses />;
  }

  return (
    <div className="space-y-5" data-testid="tenants-page">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)]">Clientes</div>
          <h1 className="text-2xl font-semibold tracking-tight">Cadastro de clientes</h1>
        </div>
        <button className="sbc-btn sbc-btn-primary" onClick={() => open()}>
          <Plus size={14} /> Adicionar
        </button>
      </div>

      <div className="sbc-card overflow-hidden">
        <div className="overflow-auto max-h-[70vh]">
          <table className="sbc-table">
            <thead>
              <tr>
                <th>Codigo</th>
                <th>Cliente</th>
                <th>Roteamento</th>
                <th>Limitacoes</th>
                <th>Status</th>
                <th className="w-24 text-right">Acoes</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} className="text-center text-[color:var(--text-muted)] py-8">Carregando...</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={6} className="text-center text-[color:var(--text-muted)] py-8">Nenhum registro.</td></tr>
              ) : rows.map((row) => (
                <tr key={row.id}>
                  <td className="font-mono text-xs">{row.codigo_cliente || "-"}</td>
                  <td>
                    <div className="font-medium">{row.nome}</div>
                    <div className="text-[10px] text-[color:var(--text-muted)]">{row.descricao || "Sem descricao"}</div>
                    <div className="font-mono text-[10px] text-[color:var(--text-muted)]">{row.primary_admin_email || "sem admin principal"}</div>
                  </td>
                  <td className="font-mono text-xs">
                    <div>{row.sip_auth_prefix || "sem prefixo"}</div>
                    <div>{(row.domains || []).join(", ") || "-"}</div>
                    <div className="text-[color:var(--text-muted)]">DDD {row.ddd || "--"} | CID {row.outbound_cid || "--"}</div>
                  </td>
                  <td className="font-mono text-xs">
                    <div>{row.max_extensions ?? 0} ramais | {row.max_trunks ?? 0} troncos | {row.max_channels ?? 0} canais SIP Trunk</div>
                    <div>{row.max_queues ?? row.max_callcenter ?? 0} filas | {row.max_agents ?? 0} agentes | {row.max_supervisors ?? 0} supervisores</div>
                    <div>{row.max_queue_resources ?? 0} recursos de fila | {row.max_ivrs ?? 0} IVRs</div>
                  </td>
                  <td>
                    <div className={`sbc-tag ${row.enabled ? "tag-online" : "tag-muted"}`}>{row.enabled ? "Ativo" : "Inativo"}</div>
                    <div className="text-[10px] text-[color:var(--text-muted)] mt-1">{row.license_status || "active"}</div>
                  </td>
                  <td className="text-right">
                    <div className="inline-flex gap-1">
                      <button className="sbc-btn" onClick={() => open(row)} title="Editar"><Edit3 size={12} /></button>
                      <button className="sbc-btn sbc-btn-danger" onClick={() => remove(row.id)} title="Excluir"><Trash2 size={12} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black/70 z-40 flex items-center justify-center p-4" onClick={close}>
          <div className="sbc-card w-full max-w-6xl max-h-[92vh] p-0 fade-in overflow-hidden flex flex-col" onClick={(event) => event.stopPropagation()}>
            <div className="flex items-center justify-between px-4 md:px-6 py-4 border-b border-[color:var(--border)] shrink-0">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)]">{editing ? "Editar" : "Novo"}</div>
                <h2 className="text-lg font-semibold">Cadastro de cliente</h2>
              </div>
              <button className="sbc-btn" onClick={close}><X size={14} /></button>
            </div>

            <div className="px-4 md:px-6 pt-4 border-b border-[color:var(--border)] flex gap-4 shrink-0 overflow-x-auto">
              {TABS.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTab(tab.id)}
                  className={`pb-3 text-xs uppercase tracking-widest ${activeTab === tab.id ? "text-[color:var(--accent-blue)] border-b-2 border-[color:var(--accent-blue)]" : "text-[color:var(--text-muted)]"}`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            <form onSubmit={save} className="p-4 md:p-6 space-y-6 overflow-y-auto min-h-0">
              {activeTab === "general" && (
                <>
                  <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
                    <Field label="Nome">
                      <input className="sbc-input" value={form.nome} onChange={(e) => setField("nome", e.target.value)} />
                    </Field>
                    <Field label="Prefixo">
                      <input className="sbc-input font-mono" value={form.sip_auth_prefix} onChange={(e) => setField("sip_auth_prefix", e.target.value)} placeholder="Gerar automaticamente" />
                    </Field>
                    <Field label="Descricao">
                      <input className="sbc-input" value={form.descricao} onChange={(e) => setField("descricao", e.target.value)} />
                    </Field>
                    <Field label="Habilitada">
                      <Toggle value={!!form.enabled} onChange={(value) => setField("enabled", value)} />
                    </Field>
                  </div>

                  <div className="border-t border-[color:var(--border)] pt-5">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <div className="text-sm font-semibold">Unidades / filiais</div>
                        <div className="text-[10px] text-[color:var(--text-muted)] font-mono">Cada filial pode usar um prefixo SIP proprio. Se ficar vazio, usa o prefixo principal do cliente.</div>
                      </div>
                      <button type="button" className="sbc-btn" onClick={addBranch}>
                        <Plus size={12} /> Adicionar filial
                      </button>
                    </div>
                    <div className="space-y-3">
                      {(form.branches || []).length === 0 && (
                        <div className="text-[11px] text-[color:var(--text-muted)] border border-dashed border-[color:var(--border)] rounded-sm px-3 py-4">
                          Nenhuma filial cadastrada.
                        </div>
                      )}
                      {(form.branches || []).map((branch, index) => (
                        <div key={`${branch.id || "branch"}-${index}`} className="grid grid-cols-1 xl:grid-cols-12 gap-3 border border-[color:var(--border)] rounded-sm p-3">
                          <div className="xl:col-span-2">
                            <label className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)] font-semibold block mb-1.5">Codigo</label>
                            <input className="sbc-input font-mono" value={branch.id || ""} onChange={(e) => updateBranch(index, "id", e.target.value)} placeholder="sede" />
                          </div>
                          <div className="xl:col-span-12 border-t border-[color:var(--border)] pt-3">
                            <div className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)] font-semibold mb-2">Comunicacao com outras filiais</div>
                            <div className="text-[10px] text-[color:var(--text-muted)] mb-2">Por padrao, ramais de filiais diferentes nao se comunicam. Selecione as unidades que esta filial pode chamar.</div>
                            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2">
                              {(form.branches || []).map((otherBranch, otherIndex) => {
                                if (otherIndex === index || !String(otherBranch.nome || "").trim()) return null;
                                const otherId = String(otherBranch.id || "").trim();
                                if (!otherId) return null;
                                const selected = (branch.allowed_branch_ids || []).includes(otherId);
                                return (
                                  <label key={`${otherId}-${otherIndex}`} className="flex items-center gap-2 text-xs border border-[color:var(--border)] rounded-sm px-2 py-2">
                                    <input
                                      type="checkbox"
                                      checked={selected}
                                      onChange={(event) => updateBranch(index, "allowed_branch_ids", event.target.checked
                                        ? [...new Set([...(branch.allowed_branch_ids || []), otherId])]
                                        : (branch.allowed_branch_ids || []).filter((value) => value !== otherId))}
                                    />
                                    <span>{otherBranch.nome} <span className="font-mono text-[color:var(--text-muted)]">({otherId})</span></span>
                                  </label>
                                );
                              })}
                            </div>
                          </div>
                          <div className="xl:col-span-2">
                            <label className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)] font-semibold block mb-1.5">Canais de entrada</label>
                            <input className="sbc-input font-mono" type="number" min="0" value={branch.max_channels_inbound || 0} onChange={(e) => updateBranch(index, "max_channels_inbound", e.target.value)} placeholder="Usar limite do cliente" />
                          </div>
                          <div className="xl:col-span-2">
                            <label className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)] font-semibold block mb-1.5">Canais de saida</label>
                            <input className="sbc-input font-mono" type="number" min="0" value={branch.max_channels_outbound || 0} onChange={(e) => updateBranch(index, "max_channels_outbound", e.target.value)} placeholder="Usar limite do cliente" />
                          </div>
                          <div className="xl:col-span-3">
                            <label className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)] font-semibold block mb-1.5">Nome da filial</label>
                            <input className="sbc-input" value={branch.nome || ""} onChange={(e) => updateBranch(index, "nome", e.target.value)} placeholder="SEDE" />
                          </div>
                          <div className="xl:col-span-3">
                            <label className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)] font-semibold block mb-1.5">Descricao</label>
                            <input className="sbc-input" value={branch.descricao || ""} onChange={(e) => updateBranch(index, "descricao", e.target.value)} placeholder="Unidade principal" />
                          </div>
                          <div className="xl:col-span-2">
                            <label className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)] font-semibold block mb-1.5">Prefixo SIP</label>
                            <input className="sbc-input font-mono" value={branch.sip_auth_prefix || ""} onChange={(e) => updateBranch(index, "sip_auth_prefix", e.target.value)} placeholder="55712106-" />
                          </div>
                          <div className="xl:col-span-1">
                            <label className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)] font-semibold block mb-1.5">Status</label>
                            <Toggle value={!!branch.enabled} onChange={(value) => updateBranch(index, "enabled", value)} />
                          </div>
                          <div className="xl:col-span-1 flex items-end justify-end">
                            <button type="button" className="sbc-btn sbc-btn-danger" onClick={() => removeBranch(index)} title="Excluir filial">
                              <Trash2 size={12} />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="border-t border-[color:var(--border)] pt-5">
                    <div className="text-sm font-semibold mb-4">Administrador do inquilino</div>
                    <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
                      <Field label="Atribuir a usuario existente">
                        <Toggle value={!!form.assign_existing_admin} onChange={(value) => setField("assign_existing_admin", value)} />
                      </Field>
                      <Field label="Perfil">
                        <select className="sbc-input" value={form.admin_profile} onChange={(e) => setField("admin_profile", e.target.value)}>
                          <option value="tenant_admin">Tenant Administrator</option>
                          <option value="admin">Administrator</option>
                        </select>
                      </Field>
                      <Field label="E-mail do administrador">
                        <input className="sbc-input" value={form.admin_email} onChange={(e) => setField("admin_email", e.target.value)} />
                      </Field>
                      <Field label="Dialogo de inicializacao">
                        <select className="sbc-input" value={form.startup_dialog} onChange={(e) => setField("startup_dialog", e.target.value)}>
                          <option value="dashboard">Painel de Controle</option>
                          <option value="ramais">Ramais</option>
                          <option value="call-center">Call Center</option>
                        </select>
                      </Field>
                      <Field label="Senha do administrador">
                        <div className="flex gap-2">
                          <input
                            className="sbc-input font-mono flex-1"
                            type={showAdminPassword ? "text" : "password"}
                            value={form.admin_password}
                            onChange={(e) => setField("admin_password", e.target.value)}
                          />
                          <button type="button" className="sbc-btn" onClick={() => setShowAdminPassword((value) => !value)} title={showAdminPassword ? "Ocultar senha" : "Mostrar senha"}>
                            {showAdminPassword ? <EyeOff size={12} /> : <Eye size={12} />}
                          </button>
                        </div>
                      </Field>
                      <Field label="Enviar e-mail de boas-vindas">
                        <Toggle value={!!form.send_welcome_email} onChange={(value) => setField("send_welcome_email", value)} />
                      </Field>
                      <Field label="Nome completo">
                        <input className="sbc-input" value={form.admin_name} onChange={(e) => setField("admin_name", e.target.value)} />
                      </Field>
                  <Field label="Slug">
                    <input className="sbc-input font-mono" value={form.slug} onChange={(e) => setField("slug", e.target.value)} placeholder="grupo-icore" />
                  </Field>
                  <Field label="Monitoramento publico">
                    <Toggle value={!!form.public_monitor_enabled} onChange={(value) => setField("public_monitor_enabled", value)} />
                  </Field>
                  <Field label="Usuario do monitoramento">
                    <input className="sbc-input font-mono" value={form.public_monitor_username || ""} onChange={(e) => setField("public_monitor_username", e.target.value)} placeholder="cliente.monitor" />
                  </Field>
                  <Field label="Senha do monitoramento" hint={editing ? "Preencha apenas se quiser trocar a senha atual." : "Minimo de 6 caracteres"}>
                    <div className="flex gap-2">
                      <input
                        className="sbc-input font-mono flex-1"
                        type={showPublicMonitorPassword ? "text" : "password"}
                        value={form.public_monitor_password || ""}
                        onChange={(e) => setField("public_monitor_password", e.target.value)}
                        placeholder={editing ? "Manter senha atual" : "******"}
                      />
                      <button type="button" className="sbc-btn" onClick={() => setShowPublicMonitorPassword((value) => !value)} title={showPublicMonitorPassword ? "Ocultar senha" : "Mostrar senha"}>
                        {showPublicMonitorPassword ? <EyeOff size={12} /> : <Eye size={12} />}
                      </button>
                    </div>
                  </Field>
                  <Field label="URL publica" full hint="Link de acesso somente leitura para o cliente">
                    <input
                      className="sbc-input font-mono"
                      value={form.slug ? `${window.location.origin}/monitor/${form.slug}` : ""}
                      readOnly
                      placeholder="A URL aparece ao informar o slug"
                    />
                  </Field>
                </div>
                  </div>

                  <div className="border-t border-[color:var(--border)] pt-5">
                    <div className="text-sm font-semibold mb-4">Limitacoes</div>
                    <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
                      <Field label="Ramais"><input className="sbc-input font-mono" type="number" value={form.max_extensions} onChange={(e) => setField("max_extensions", e.target.value)} /></Field>
                      <Field label="Conferencias"><input className="sbc-input font-mono" type="number" value={form.max_conferences} onChange={(e) => setField("max_conferences", e.target.value)} /></Field>
                      <Field label="Troncos"><input className="sbc-input font-mono" type="number" value={form.max_trunks} onChange={(e) => setField("max_trunks", e.target.value)} /></Field>
                      <Field label="Estacionamentos"><input className="sbc-input font-mono" type="number" value={form.max_parkings} onChange={(e) => setField("max_parkings", e.target.value)} /></Field>
                      <Field label="Filas"><input className="sbc-input font-mono" type="number" value={form.max_queues} onChange={(e) => { setField("max_queues", e.target.value); setField("max_callcenter", e.target.value); }} /></Field>
                      <Field label="Connect Devices"><input className="sbc-input font-mono" type="number" value={form.max_connect_devices} onChange={(e) => setField("max_connect_devices", e.target.value)} /></Field>
                      <Field label="IVRs"><input className="sbc-input font-mono" type="number" value={form.max_ivrs} onChange={(e) => setField("max_ivrs", e.target.value)} /></Field>
                      <Field label="Permitir gravacoes"><Toggle value={!!form.recording_enabled} onChange={(value) => setField("recording_enabled", value)} /></Field>
                    </div>
                  </div>
                </>
              )}

              {activeTab === "routing" && (
                <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
                  <Field label="Dominios SIP" full hint="Separe por virgula">
                    <input className="sbc-input font-mono" value={form.domains} onChange={(e) => setField("domains", e.target.value)} placeholder="cliente.exemplo.com, pbx.cliente.com" />
                  </Field>
                  <Field label="IPs permitidos" full hint="Separe por virgula">
                    <input className="sbc-input font-mono" value={form.ips} onChange={(e) => setField("ips", e.target.value)} placeholder="203.0.113.10, 198.51.100.20" />
                  </Field>
                  <Field label="Usar prefixo no login SIP">
                    <Toggle value={!!form.use_prefixed_sip_auth} onChange={(value) => setField("use_prefixed_sip_auth", value)} />
                  </Field>
                  <Field label="Permitir registro por IP">
                    <Toggle value={!!form.allow_ip_registration} onChange={(value) => setField("allow_ip_registration", value)} />
                  </Field>
                  <Field label="DDD do cliente">
                    <input className="sbc-input font-mono" value={form.ddd} onChange={(e) => setField("ddd", e.target.value)} placeholder="71" />
                  </Field>
                  <Field label="CID padrao de saida">
                    <input className="sbc-input font-mono" value={form.outbound_cid} onChange={(e) => setField("outbound_cid", e.target.value)} placeholder="+557135999160" />
                  </Field>
                </div>
              )}

              {activeTab === "config" && (
                <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
                  <Field label="Licenca">
                    <select className="sbc-input" value={form.license_status} onChange={(e) => setField("license_status", e.target.value)}>
                      <option value="active">Ativa</option>
                      <option value="suspended">Suspensa</option>
                      <option value="expired">Expirada</option>
                    </select>
                  </Field>
                  <Field label="Retencao de gravacoes">
                    <select className="sbc-input" value={form.recording_retention_days} onChange={(e) => setField("recording_retention_days", e.target.value)}>
                      <option value={7}>7 dias</option>
                      <option value={30}>30 dias</option>
                      <option value={90}>90 dias</option>
                      <option value={180}>180 dias</option>
                      <option value={365}>1 ano</option>
                    </select>
                  </Field>
                  <Field label="Validade da licenca" hint="Formato ISO opcional">
                    <input className="sbc-input font-mono" value={form.license_expires_at} onChange={(e) => setField("license_expires_at", e.target.value)} placeholder="2027-12-31T23:59:59+00:00" />
                  </Field>
                  <Field label="Canais SIP Trunk simultaneos">
                    <input className="sbc-input font-mono" type="number" value={form.max_channels} onChange={(e) => setField("max_channels", e.target.value)} />
                  </Field>
                  <Field label="Canais de entrada" hint="Zero usa o limite geral acima">
                    <input className="sbc-input font-mono" type="number" min="0" value={form.max_channels_inbound} onChange={(e) => setField("max_channels_inbound", e.target.value)} />
                  </Field>
                  <Field label="Canais de saida" hint="Zero usa o limite geral acima">
                    <input className="sbc-input font-mono" type="number" min="0" value={form.max_channels_outbound} onChange={(e) => setField("max_channels_outbound", e.target.value)} />
                  </Field>
                  <Field label="Agentes">
                    <input className="sbc-input font-mono" type="number" value={form.max_agents} onChange={(e) => setField("max_agents", e.target.value)} />
                  </Field>
                  <Field label="Supervisores">
                    <input className="sbc-input font-mono" type="number" value={form.max_supervisors} onChange={(e) => setField("max_supervisors", e.target.value)} />
                  </Field>
                  <Field label="Filas">
                    <input className="sbc-input font-mono" type="number" value={form.max_queues} onChange={(e) => { setField("max_queues", e.target.value); setField("max_callcenter", e.target.value); }} />
                  </Field>
                  <Field label="Recursos de fila">
                    <input className="sbc-input font-mono" type="number" value={form.max_queue_resources} onChange={(e) => setField("max_queue_resources", e.target.value)} />
                  </Field>
                </div>
              )}

              {error && (
                <div className="text-xs text-[color:var(--accent-red)] font-mono border border-[rgba(239,68,68,0.3)] bg-[rgba(239,68,68,0.08)] px-3 py-2 rounded-sm">
                  ! {error}
                </div>
              )}

              <div className="flex justify-end gap-2 pt-3 border-t border-[color:var(--border)] sticky bottom-0 bg-[color:var(--panel)]">
                <button type="button" className="sbc-btn" onClick={close}>Cancelar</button>
                <button type="submit" className="sbc-btn sbc-btn-primary" disabled={saving}>
                  {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
                  {saving ? "Salvando..." : "Salvar"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

